import { createTheme } from '@mui/material';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import { sgrColors } from './SgrColors';

export const INITIAL_PAGE_SIZE = 10;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: sgrColors.navy,
    },
    secondary: {
      main: sgrColors.azure,
    },
    warning: {
      main: sgrColors.warning,
    },
    info: {
      main: sgrColors.navy,
    },
  },

  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '95% !important',
          '@media (max-width:600px)': {
            maxWidth: '100% !important',
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '0px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: '2px',
          borderRadius: '0px',
        },
      },
    },
    MuiListItemButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: '2px',
          borderRadius: '0px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '0px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: '0px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '0px',
        },
      },
    },
    MuiToggleButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '0px',
          '&.Mui-selected': {
            color: sgrColors.navy,
            backgroundColor: 'rgba(0, 81, 157, 0.08)',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiChip-root': {
            backgroundColor: `${sgrColors.chips_background} !important`,
          },
          '& .MuiChip-deleteIcon': { color: `${sgrColors.navy} !important` },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeaders': {
            padding: '0.5rem 0',
            borderBlockEnd: '1px solid #B7BFCD',
            backgroundImage:
              'linear-gradient(to bottom, rgba(255, 255, 255, 0.8) 0%, rgba(211, 221, 229, 0.4) 100%)',
            '& div[role=row]': {
              background: 'none !important',
            },
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            '&:hover, &.selected': {
              backgroundColor: sgrColors.hover_sky_blue,
            },
          },
          '& .MuiDataGrid-row.expiring-contract': {
            backgroundColor: sgrColors.light_red,
          },
          '& .MuiDataGrid-row.expired-contract': {
            backgroundColor: sgrColors.disabled,
          },
          '& .MuiDataGrid-booleanCell[data-value="false"]': {
            color: sgrColors.warning,
          },
          '& .MuiDataGrid-booleanCell[data-value="true"]': {
            color: sgrColors.success,
          },
          '& .MuiDataGrid-footerContainer': {
            borderBlockStart: '1px solid #B7BFCD',
            backgroundImage:
              'linear-gradient(to top, rgba(255, 255, 255, 0.8) 0%, rgba(211, 221, 229, 0.4) 100%)',
          },
        },
        cell: {
          fontFamily: 'sgr-albert-sans, sans-serif',
          fontSize: '0.875rem',
        },
        columnHeader: {
          textTransform: 'capitalize',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          opacity: 1,
          fontFamily: 'sgr-albert-sans, sans-serif',
          '&.Mui-selected': {
            color: sgrColors.navy,
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: 'h6', align: 'left' },
        subheaderTypographyProps: {
          variant: 'caption',
          color: 'textSecondary',
          align: 'left',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          textAlign: 'left',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiDialogTitle-root+.MuiDialogContent-root': {
            paddingTop: '1rem',
          },
        },
      },
    },
  },

  typography: {
    h1: {
      fontSize: '3rem',
      fontFamily: 'sgr-frank-ruh-libre,  sans-serif',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h2: {
      fontSize: '2.5rem',
      fontFamily: 'sgr-frank-ruh-libre,  sans-serif',
      '@media (max-width:600px)': {
        fontSize: '1.6rem',
      },
    },
    h3: {
      fontSize: '2rem',
      fontFamily: 'sgr-frank-ruh-libre,  sans-serif',
      '@media (max-width:600px)': {
        fontSize: '1.4rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontFamily: 'sgr-albert-sans, sans-serif',
      '@media (max-width:600px)': {
        fontSize: '1.2rem',
      },
    },
    h5: {
      fontSize: '1.25rem',
      fontFamily: 'sgr-albert-sans, sans-serif',
    },
    h6: {
      fontSize: '1rem',
      fontFamily: 'sgr-albert-sans, sans-serif',
      fontWeight: 500,
    },
    subtitle1: {
      fontFamily: 'sgr-albert-sans, sans-serif',
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: 'sgr-albert-sans, sans-serif',
      fontWeight: 300,
    },
    body1: {
      fontFamily: 'sgr-albert-sans, sans-serif',
    },
    body2: {
      fontFamily: 'sgr-albert-sans, sans-serif',
    },
    caption: {
      fontFamily: 'sgr-albert-sans, sans-serif',
      lineHeight: '100%',
    },
  },
});
